import * as React from 'react';

// API
import apibridge from '../../apibridge';

// REDUX
import { useAppSelector } from '../../store';

// UTILS
import { validationErrorsToString } from '../../utils';
import { useUpdateTotalNotification } from '../../utils/useUpdateTotalNotification';

// PACKAGES
import { SwitchTransition, CSSTransition } from 'react-transition-group';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';

// COMPONENTS
import Button from '../../components/Button';
import LoadingSpinner from '../../components/LoadingSpinner';
import FormErrorMessage from '../../components/ErrorMessage';

// ICONS
import { ReactComponent as AlertIcon } from '../../assets/svg/alert.svg';

// TYPES
import { closeModalType } from '../../components/GlobalModal';
import { IncidentAcknowledgeCommand } from '../../api/models';

interface AcknowledgeModalTypes {
	getIncidentReportsDetailsData: () => Promise<void>;
	closeModal?: closeModalType;
	incidentId?: string;
	title?: string;
}

const AcknowledgeModal: React.FC<AcknowledgeModalTypes> = ({
	getIncidentReportsDetailsData,
	closeModal,
	incidentId,
	title
}) => {
	// Error and loading state
	const [formErrorMessage, setFormErrorMessage] = React.useState<string>('');
	const [isLoading, setIsLoading] = React.useState<boolean>(false);
	const [showError, setShowError] = React.useState<boolean>(false);

	const acknowledgeYourself = async () => {
		setIsLoading(true);

		const data: IncidentAcknowledgeCommand = { incidentId: incidentId };

		const response = await apibridge.acknowledgeYourself(data);

		if (response.isError) {
			setIsLoading(false);

			setFormErrorMessage(
				response.validationErrors.length
					? validationErrorsToString(response.validationErrors)
					: response.message || 'An error has occurred'
			);
		} else {
			setIsLoading(false);

			if (closeModal) {
				closeModal();
			}

			getIncidentReportsDetailsData();

			useUpdateTotalNotification();
		}
	};

	const initialValues = {
		informationConfirmed: undefined,
		signatureConfirmed: undefined
	};

	const validationSchema = Yup.object({
		informationConfirmed: Yup.bool().oneOf([true], 'Required').required('Required'),
		signatureConfirmed: Yup.bool().oneOf([true], 'Required').required('Required')
	});

	const submitHandler = (values: any) => {
		acknowledgeYourself();
	};

	// Logic to work out current User Detail
	const activeProfileId = useAppSelector((state) => state.user.activeProfileId);
	const profiles = useAppSelector((state) => state.user.profiles);
	const activeProfile = profiles?.find((item) => item.id === activeProfileId);

	const firstName = useAppSelector((state) => state.user.firstName);
	const lastName = useAppSelector((state) => state.user.lastName);
	const currentRole = activeProfile?.roleName;

	return (
		<Formik
			initialValues={initialValues}
			validationSchema={validationSchema}
			onSubmit={submitHandler}
			enableReinitialize
		>
			{(formik) => {
				return (
					<Form translate="yes" className="general-form mtop-0">
						<>
							{showError ? (
								<FormErrorMessage message={formErrorMessage} />
							) : (
								<SwitchTransition mode="out-in">
									<CSSTransition
										key={`key_${isLoading}`}
										timeout={600}
										classNames="standalone-fade"
										mountOnEnter
										unmountOnExit
									>
										{isLoading ? (
											<LoadingSpinner show={isLoading} />
										) : (
											<>
												<div className="complaints-detail w-100">
													<div className="detail-block row mbtom-40">
														<div className="col-6">
															<h4 className="detail-heading">Signee</h4>

															<p className="detail-info">
																{firstName} {lastName} (yourself)
															</p>
														</div>

														<div className="col-6">
															<h4 className="detail-heading">Role</h4>

															<p className="detail-info">{currentRole}</p>
														</div>
													</div>
												</div>

												<div className="incident-electronic-acknowledgement mbtom-40">
													<p className="sub-grey-text">Electronic Signature Acknowledgement</p>

													<p className="signature">
														{firstName} {lastName}
													</p>
												</div>

												<div className="form-group">
													<label className="form-control-checkbox">
														<Field
															name="informationConfirmed"
															type="checkbox"
															onChange={() => {
																formik.setFieldValue('informationConfirmed', !formik.values.informationConfirmed);
															}}
														/>

														<span>
															I acknowledge that I have been successfully notified of this incident, injury, trauma or
															illness and have read and understood the information in this incident report.
														</span>
													</label>

													<ErrorMessage name="informationConfirmed">
														{(error) => <div className="form-validation-error">{error}</div>}
													</ErrorMessage>
												</div>

												<div className="form-group">
													<label className="form-control-checkbox">
														<Field
															name="signatureConfirmed"
															type="checkbox"
															onChange={() => {
																formik.setFieldValue('signatureConfirmed', !formik.values.signatureConfirmed);
															}}
														/>

														<span>
															I understand that by signing the Electronic Signature Acknowledgement, I consent to the use of an
															electronic signature as the legal equivalent of my manual/handwritten signature.
														</span>
													</label>

													<ErrorMessage name="signatureConfirmed">
														{(error) => <div className="form-validation-error">{error}</div>}
													</ErrorMessage>
												</div>

												<Button className="btn-primary full-width" type="submit">
													Submit acknowledgement
												</Button>
											</>
										)}
									</CSSTransition>
								</SwitchTransition>
							)}
						</>
					</Form>
				);
			}}
		</Formik>
	);
};

export default AcknowledgeModal;
